/**
 * Authentication API Service
 * 
 * Handles login, logout, and token refresh with the backend.
 */

import { httpClient, setAuthToken, clearAuthToken, getStoredUser, ApiError } from './HttpClient';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  data: {
    token: string;
    user: {
      id: string;
      email: string;
      name: string;
      role: 'admin' | 'agent' | 'support';
      agentId?: string;
    };
  };
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'agent' | 'support';
  agentId?: string;
}

/**
 * Login with email and password
 */
export async function login(email: string, password: string): Promise<AuthUser> {
  const response = await httpClient.post<LoginResponse>('/auth/login', { email, password }, { skipAuth: true });
  
  if (response.success && response.data) {
    setAuthToken(response.data.token, response.data.user);
    return response.data.user;
  }
  
  throw new ApiError(401, 'LOGIN_FAILED', 'Login failed');
}

/**
 * Refresh auth token
 */
export async function refreshToken(): Promise<AuthUser> {
  const response = await httpClient.post<LoginResponse>('/auth/refresh');
  
  if (response.success && response.data) {
    setAuthToken(response.data.token, response.data.user);
    return response.data.user;
  }
  
  throw new ApiError(401, 'REFRESH_FAILED', 'Token refresh failed');
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
  try {
    await httpClient.post('/auth/logout');
  } catch {
    // Ignore logout errors - clear local token anyway
  }
  clearAuthToken();
}

/**
 * Get current user from local storage
 */
export function getCurrentUser(): AuthUser | null {
  return getStoredUser();
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getCurrentUser() !== null;
}
